import Header from '@/components/Header';
import Footer from '@/components/Footer';
import LogoBigColor from '@/icons/LogoBigColor';
import MobileBottomNav from '@/components/MobileBottomNav';
import { IconArrowRight as ArrowRight } from '@tabler/icons-react';
import { PageHero, bodyMuted, card, cardTitle, eyebrow, pageBg, sectionTitle, surface, wrap } from '@/components/ui/design';

const serviceList = 'mt-5 space-y-3 text-base leading-relaxed text-grey40 dark:text-nightMuted';
const emojiTile = 'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-grey99 text-2xl ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/10';

export default function Aboutus() {
    return (
        <div className={`min-h-screen flex flex-col ${pageBg}`}>
            <Header />

            <main className="flex-1">
                <PageHero
                    eyebrow={<LogoBigColor width={160} height={45} />}
                    title="About Us"
                    subtitle="Transforming how events are planned, promoted, and experienced with cutting-edge technology and creative solutions."
                />

                <section className={`${wrap} mt-20 sm:mt-28`}>
                    <div className="grid gap-8 lg:grid-cols-[1fr_1.4fr] lg:gap-16">
                        <h2 className={sectionTitle}>Our Story</h2>
                        <div className="space-y-5">
                            <p className={`${bodyMuted} text-lg`}>
                                At EventUp.ai, we're transforming how events are planned, promoted, and experienced.
                                Founded in Irvine, California, our mission is to make event planning easier, smarter, and more cost-efficient —
                                all while delivering premium support, creative design, and cutting-edge technology for every organizer.
                            </p>
                            <p className={`${bodyMuted} text-lg`}>
                                We believe that successful events combine great technology, powerful marketing, and beautiful design.
                                That's why we built EventUp.ai — an all-in-one platform that helps you manage your event from concept
                                to completion with ease and confidence.
                            </p>
                        </div>
                    </div>
                </section>

                <section className={`${wrap} mt-24 sm:mt-32`}>
                    <h2 className={sectionTitle}>What We Do</h2>
                    <p className={`${bodyMuted} mt-5 max-w-[620px] text-lg`}>
                        We go beyond ticketing — we provide complete event solutions that cover everything you need to make your event a success.
                    </p>

                    <div className="mt-10 grid gap-4 md:grid-cols-2">
                        <div className={`${card} p-6 sm:p-8`}>
                            <div className="flex items-center gap-4">
                                <span className={emojiTile}>🎟️</span>
                                <h3 className={cardTitle}>Ticketing & Technology</h3>
                            </div>
                            <ul className={serviceList}>
                                <li>• AI-powered ticketing system for events of any size</li>
                                <li>• Real-time analytics, sales insights, and secure checkout</li>
                                <li>• Seamless website integration — sell tickets directly on your own website without redirecting users</li>
                                <li>• Full technical setup and continuous event-day support</li>
                            </ul>
                        </div>

                        <div className={`${card} p-6 sm:p-8`}>
                            <div className="flex items-center gap-4">
                                <span className={emojiTile}>🎨</span>
                                <h3 className={cardTitle}>Design & Creative Services</h3>
                            </div>
                            <ul className={serviceList}>
                                <li>• Custom poster design, branding, and motion graphics tailored to your event</li>
                                <li>• Professionally designed visuals to attract your target audience</li>
                                <li>• Creative consultation to align your event's visual identity and messaging</li>
                            </ul>
                        </div>

                        <div className={`${card} p-6 sm:p-8`}>
                            <div className="flex items-center gap-4">
                                <span className={emojiTile}>📣</span>
                                <h3 className={cardTitle}>Marketing & Promotion</h3>
                            </div>
                            <ul className={serviceList}>
                                <li>• Comprehensive digital marketing and advertising strategies</li>
                                <li>• Social media promotion and audience targeting</li>
                                <li>• Campaign planning to increase awareness and maximize ticket sales</li>
                            </ul>
                        </div>

                        <div className={`${card} p-6 sm:p-8`}>
                            <div className="flex items-center gap-4">
                                <span className={emojiTile}>🏛️</span>
                                <h3 className={cardTitle}>Event Planning Support</h3>
                            </div>
                            <ul className={serviceList}>
                                <li>• Assistance with venue selection, vendor coordination, and negotiation</li>
                                <li>• End-to-end event guidance from planning to execution</li>
                                <li>• Dedicated support team that works with you at every step</li>
                            </ul>
                        </div>
                    </div>
                </section>

                <section className={`${wrap} mt-24 sm:mt-32`}>
                    <div className={`${surface} px-6 py-10 sm:px-12 sm:py-14`}>
                        <h2 className={sectionTitle}>💙 Nonprofit & NGO Partnerships</h2>
                        <div className="mt-6 grid gap-5 md:grid-cols-2 md:gap-10">
                            <p className={`${bodyMuted} text-lg`}>
                                We're proud to support nonprofit organizations and NGOs with special pricing packages designed to help
                                community-driven events thrive.
                            </p>
                            <p className={`${bodyMuted} text-lg`}>
                                Our goal is to empower organizations that make a difference — by giving them the same professional
                                tools and premium support used by large-scale event organizers.
                            </p>
                        </div>
                    </div>
                </section>

                <section className={`${wrap} mt-24 sm:mt-32`}>
                    <div className="grid gap-8 lg:grid-cols-[1fr_1.4fr] lg:gap-16">
                        <h2 className={sectionTitle}>Our Commitment</h2>
                        <div className="space-y-5">
                            <p className={`${bodyMuted} text-lg`}>
                                At EventUp.ai, we're more than a ticketing platform — we're your creative, technical, and strategic partner.
                                Our team combines design, marketing, and technology expertise to ensure your event is effortless,
                                efficient, and truly memorable.
                            </p>
                            <p className={`${bodyMuted} text-lg`}>
                                Whether you're organizing a cultural celebration, charity fundraiser, conference, or live performance,
                                we'll be with you every step of the way — helping you plan, promote, and deliver an exceptional event experience.
                            </p>
                            <p className="text-2xl font-bold leading-tight tracking-[-0.03em] text-primary40 dark:text-nightAccent">
                                Let's make your next event extraordinary — together with EventUp.ai.
                            </p>
                        </div>
                    </div>
                </section>

                <section className={`${wrap} mt-24 mb-20 sm:mt-32`}>
                    <div className="relative isolate overflow-hidden rounded-[36px] bg-[#0a0a0a] px-6 py-12 text-white dark:bg-[#101010] dark:ring-1 dark:ring-white/10 sm:px-12 sm:py-16 lg:px-16 lg:py-20">
                        <div className="pointer-events-none absolute -right-32 -top-32 -z-10 h-[420px] w-[420px] rounded-full bg-nightAccent/30 blur-[100px] max-md:hidden" />
                        <div className="pointer-events-none absolute -bottom-40 left-1/4 -z-10 h-[360px] w-[360px] rounded-full bg-[#6d4aff]/25 blur-[100px] max-md:hidden" />
                        <div className="grid gap-12 lg:grid-cols-[1.1fr_1fr] lg:items-center">
                            <div>
                                <h2 className="text-[clamp(40px,5.5vw,76px)] font-bold leading-[0.95] tracking-[-0.05em]">Get in Touch</h2>
                                <p className="mt-6 max-w-md text-lg leading-relaxed text-white/60">
                                    Ready to transform your next event? We'd love to hear from you and discuss how we can help make your vision a reality.
                                </p>
                                <div className="mt-8">
                                    <a
                                        href="/"
                                        className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-nightAccent px-7 text-base font-semibold text-nightBg transition-colors hover:bg-white"
                                    >
                                        Back to Home <ArrowRight className="h-4 w-4" />
                                    </a>
                                </div>
                            </div>
                            <div className="space-y-3">
                                <div className="rounded-3xl bg-white/[0.05] p-6 ring-1 ring-white/10">
                                    <strong className={`${eyebrow} block !text-nightAccent`}>Email:</strong>
                                    <span className="mt-2 block break-words text-lg font-bold tracking-[-0.02em]">admin@alimotech.com</span>
                                </div>
                                <div className="rounded-3xl bg-white/[0.05] p-6 ring-1 ring-white/10">
                                    <strong className={`${eyebrow} block !text-nightAccent`}>Phone:</strong>
                                    <span className="mt-2 block text-lg font-bold tracking-[-0.02em]">(949) 431-6160</span>
                                </div>
                                <div className="rounded-3xl bg-white/[0.05] p-6 ring-1 ring-white/10">
                                    <strong className={`${eyebrow} block !text-nightAccent`}>Location:</strong>
                                    <span className="mt-2 block text-lg font-bold tracking-[-0.02em]">Aliso Viejo , CA 92656</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer hasMobileNav />
            <MobileBottomNav />
        </div>
    );
}
